import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WifiOff,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertCircle
} from 'lucide-react';
import { Box, Typography, Button, CircularProgress, Collapse, Paper } from '@mui/material';
import { checkServerHealth } from '../utils/serverHealth';
import { getCapturedClientError } from '../utils/errorReporting';

const ConnectionErrorPage = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [lastError, setLastError] = useState(null);

  useEffect(() => {
    const error = getCapturedClientError();
    setLastError(error);

    console.group('🚨 [Connection / Server Error Report]');
    if (error) {
      console.error('Message:', error.message);
      console.error('Endpoint / URL:', error.url || error.component || 'N/A');
      console.error('Method:', error.method || 'GET');
      console.error('HTTP Status:', error.status || 'No Response (Network / Outage)');
      console.error('Error Code:', error.code || 'N/A');
      console.error('Page Origin:', error.page || window.location.pathname);
      console.error('Reported At:', error.reportedAt || new Date().toISOString());
      console.error('Full Error Object:', error);
    } else {
      console.warn('No specific client error payload found in storage.');
    }
    console.groupEnd();
  }, []);

  useEffect(() => {
    const checkServer = async () => {
      const isServerUp = await checkServerHealth();
      if (isServerUp) {
        navigate('/dashboard');
      } else {
        console.log('Connection still failing...');
      }
    };

    const interval = setInterval(checkServer, 10000);
    return () => clearInterval(interval);
  }, [navigate]);

  const handleManualCheck = async () => {
    setIsChecking(true);
    const isServerUp = await checkServerHealth();
    if (isServerUp) {
      navigate('/dashboard');
    } else {
      console.log('Manual check: Connection still failing');
    }
    setTimeout(() => setIsChecking(false), 1000);
  };

  return (
    <Box sx={{
      padding: '32px',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8fafc'
    }}>
      <Box sx={{
        maxWidth: '640px',
        width: '100%',
        textAlign: 'center',
        padding: '40px',
        backgroundColor: 'white',
        borderRadius: '24px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0'
      }}>
        <Box sx={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#fee2e2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          <WifiOff size={40} color="#ef4444" />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>
          Connection Error
        </Typography>
        
        <Typography sx={{ color: '#64748b', mb: 3, lineHeight: 1.6 }}>
          We're having trouble connecting to the server. Please check your internet connection and try again.
        </Typography>

        {lastError && (
          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Button
              size="small"
              onClick={() => setShowDetails(prev => !prev)}
              endIcon={showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              startIcon={<AlertCircle size={16} color="#ef4444" />}
              sx={{
                textTransform: 'none',
                color: '#64748b',
                fontWeight: 600,
                fontSize: '0.8rem',
                p: 0.5
              }}
            >
              {showDetails ? 'Hide Technical Error Details' : 'View Technical Error Details'}
            </Button>
            <Collapse in={showDetails}>
              <Paper
                variant="outlined"
                sx={{
                  mt: 1,
                  p: 2,
                  bgcolor: '#f8fafc',
                  borderColor: '#e2e8f0',
                  borderRadius: '10px',
                  fontSize: '0.78rem',
                  fontFamily: 'monospace',
                  color: '#334155',
                  overflowX: 'auto'
                }}
              >
                <div><strong>Message:</strong> {lastError.message || 'Network request failed'}</div>
                {lastError.url && <div><strong>Endpoint:</strong> {lastError.method ? `${lastError.method} ` : ''}{lastError.url}</div>}
                {lastError.status && <div><strong>Status:</strong> {lastError.status}</div>}
                {lastError.code && <div><strong>Code:</strong> {lastError.code}</div>}
                {lastError.page && <div><strong>Page:</strong> {lastError.page}</div>}
                {lastError.reportedAt && <div><strong>Time:</strong> {lastError.reportedAt}</div>}
              </Paper>
            </Collapse>
          </Box>
        )}

        <Box sx={{ 
          display: 'flex', 
          gap: '16px', 
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <Button
            variant="contained"
            startIcon={isChecking ? <CircularProgress size={20} color="inherit" /> : <RefreshCw size={20} />}
            onClick={handleManualCheck}
            disabled={isChecking}
            sx={{
              backgroundColor: '#ef4444',
              '&:hover': { backgroundColor: '#dc2626' },
              borderRadius: '12px',
              padding: '12px 24px',
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            {isChecking ? 'Checking...' : 'Try Again'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={{
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f1f5f9' },
              borderRadius: '12px',
              padding: '12px 24px',
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Refresh Page
          </Button>
        </Box>

        <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #f1f5f9' }}>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            We'll automatically reconnect you as soon as the server becomes reachable.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ConnectionErrorPage;
